import { type GstOutputRegisterRow, type GstOutputRegisterSummaryRow } from '@utazzo/shared';
import { UzBadge, UzButton, UzCard, UzInput, UzMultiSelect, UzSelect } from '@utazzo/ui';
import { Download } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

import { FyRangeControls, useFyRange } from '../components/FyRangeControls';
import { GstInvoicePaidDialog } from '../components/GstInvoicePaidDialog';
import { getGstOutputRegister } from '../invoices.api';
import { formatDateDMY, formatInrPaise } from '../invoices.helpers';
import { useMarkGstInvoicePaid } from '../use-gst-invoices';
import { useGstOutputRegister, useInvoiceAgents } from '../use-invoices';

import { PageShell } from '@/components/page-shell';
import { dateInputToIso, formatDMY } from '@/lib/date-format';

/**
 * GST sale register (route /reports/gst-output-register), rendered to the client
 * format: proforma + GST number, customer + type + POS state, service type, and
 * the money split into non-taxable / taxable / CGST-SGST-IGST (rate + value) /
 * total GST / gross sale. Credit-note rows show in red (negative amounts).
 * Filters: date range, place of supply, customer type (B2B/B2C), agent, search.
 */

const PLACE_OPTIONS = [
  { value: '', label: 'All places' },
  { value: 'INTRA', label: 'Intra-state' },
  { value: 'INTER', label: 'Inter-state' },
  { value: 'EXPORT', label: 'Export' },
] as const;

const TYPE_OPTIONS = [
  { value: '', label: 'All customer types' },
  { value: 'B2C', label: 'B2C' },
  { value: 'B2B', label: 'B2B' },
] as const;

type TypeFilter = '' | 'B2B' | 'B2C';

const PLACE_LABEL: Record<string, string> = {
  INTRA: 'Intra-state',
  INTER: 'Inter-state',
  EXPORT: 'Export',
};

/** Rupees (number, for spreadsheet math) from a signed paise string. */
const rupees = (paise: string): number => Number(paise) / 100;
/** Percent as a fraction (0.09) so Excel's % format renders 9%. */
const pctFraction = (rate: string): number => Number(rate) / 100;

const exportRegisterToXlsx = (rows: GstOutputRegisterRow[]): void => {
  const data = rows.map((r, i) => ({
    'S.NO': i + 1,
    'PROFORMA INVOICE NO': r.proformaNumber ?? '',
    'GST INVOICE NO': r.gstInvoiceNumber ?? '',
    'INVOICE DATE': formatDateDMY(r.date),
    'CUSTOMER NAME': r.customerName ?? '',
    'CUSTOMER TYPE': r.customerType,
    'POS (Customer State)': r.posState ?? '',
    'SERVICE TYPE': r.serviceType ?? '',
    'NON TAXABLE (Supplier Value)': rupees(r.nonTaxablePaise),
    'TAXABLE (Service Charge)': rupees(r.taxablePaise),
    'CGST %': pctFraction(r.cgstRate),
    'CGST VALUE': rupees(r.cgstPaise),
    'SGST %': pctFraction(r.sgstRate),
    'SGST VALUE': rupees(r.sgstPaise),
    'IGST %': pctFraction(r.igstRate),
    'IGST VALUE': rupees(r.igstPaise),
    'TOTAL GST': rupees(r.totalGstPaise),
    'GROSS SALE': rupees(r.grossSalePaise),
    'GST PAID': r.kind === 'CREDIT_NOTE' ? '' : r.gstPaid ? 'Yes' : 'No',
    'GST PAYMENT DATE': r.gstPaidAt ? formatDateDMY(r.gstPaidAt) : '',
    Document: r.kind === 'CREDIT_NOTE' ? 'Credit note' : 'Invoice',
    GSTIN: r.customerGstin ?? '',
    Slab: r.gstSlab,
    HSN: r.hsn,
    'Place of Supply': PLACE_LABEL[r.placeOfSupply] ?? r.placeOfSupply,
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'GST Register');
  XLSX.writeFile(wb, `gst-register-${new Date().toISOString().slice(0, 10)}.xlsx`);
};

type PaidFilter = 'ALL' | 'PAID' | 'UNPAID';

const FilterChip = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): React.JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    className={
      active
        ? 'border-primary bg-primary text-primary-foreground rounded-full border px-3 py-1 text-xs font-medium'
        : 'rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50'
    }
  >
    {label}
  </button>
);

/** Sum a signed paise field over rows as a BigInt string. */
const sumPaise = (
  rows: GstOutputRegisterRow[],
  pick: (r: GstOutputRegisterRow) => string,
): string => rows.reduce((s, r) => s + BigInt(pick(r)), 0n).toString();

export const GstOutputRegisterPage = (): React.JSX.Element => {
  const fyState = useFyRange();
  const { from, to } = fyState;
  const [places, setPlaces] = useState<string[]>([]);
  const [customerType, setCustomerType] = useState<TypeFilter>('');
  const [agents, setAgents] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const agentsQuery = useInvoiceAgents();

  const query = {
    from,
    to,
    ...(places.length ? { placeOfSupply: places.join(',') } : {}),
    ...(customerType ? { customerType } : {}),
    ...(agents.length ? { travelAgentName: agents.join(',') } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
  };
  const registerQuery = useGstOutputRegister(query);
  const markPaid = useMarkGstInvoicePaid();
  const [paidFilter, setPaidFilter] = useState<PaidFilter>('ALL');
  const [paidDialog, setPaidDialog] = useState<{ id: string; number: string } | null>(null);

  const data = registerQuery.data;
  const allRows = data?.rows ?? [];
  // Paid status is per-invoice; credit-note rows are not markable. The counts
  // and the paid filter apply to INVOICE rows only.
  const invoiceRows = allRows.filter((r) => r.kind === 'INVOICE');
  const paidCount = invoiceRows.filter((r) => r.gstPaid).length;
  const unpaidCount = invoiceRows.length - paidCount;
  // PAID / UNPAID narrow to matching invoice rows (credit notes drop out);
  // ALL shows everything, credit notes included.
  const rows = allRows.filter((r) => {
    if (paidFilter === 'PAID') return r.kind === 'INVOICE' && r.gstPaid;
    if (paidFilter === 'UNPAID') return r.kind === 'INVOICE' && !r.gstPaid;
    return true;
  });
  // Footer totals reflect the rows currently shown (so they stay consistent
  // when a paid filter is active).
  const footer = {
    taxablePaise: sumPaise(rows, (r) => r.taxablePaise),
    cgstPaise: sumPaise(rows, (r) => r.cgstPaise),
    sgstPaise: sumPaise(rows, (r) => r.sgstPaise),
    igstPaise: sumPaise(rows, (r) => r.igstPaise),
    totalPaise: sumPaise(rows, (r) => r.totalGstPaise),
  };

  const handleExport = async (): Promise<void> => {
    try {
      const full = await getGstOutputRegister(query as Parameters<typeof getGstOutputRegister>[0]);
      exportRegisterToXlsx(full.rows);
    } catch {
      toast.error('Failed to export the register');
    }
  };

  return (
    <PageShell
      title="GST output register"
      description="Sale register by proforma / GST invoice for a period. Credit notes reduce the net."
      actions={
        <UzButton
          variant="outline"
          leftIcon={<Download className="h-4 w-4" />}
          disabled={allRows.length === 0}
          onClick={() => void handleExport()}
        >
          Export Excel
        </UzButton>
      }
    >
      <div className="mb-3">
        <UzInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search proforma / GST no., customer, agent, GSTIN..."
          containerClassName="max-w-md"
        />
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <FyRangeControls state={fyState} />
        <div className="w-52">
          <UzMultiSelect
            label="Place of supply"
            placeholder="All places"
            values={places}
            onValuesChange={setPlaces}
            options={PLACE_OPTIONS.filter((o) => o.value).map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </div>
        <UzSelect
          label="Customer type"
          value={customerType}
          onValueChange={(v) => setCustomerType(v as TypeFilter)}
          options={TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          containerClassName="w-44"
        />
        <div className="w-64">
          <UzMultiSelect
            label="Agents"
            placeholder="All agents"
            searchPlaceholder="Search agents..."
            values={agents}
            onValuesChange={setAgents}
            options={(agentsQuery.data ?? []).map((a) => ({ value: a, label: a }))}
          />
        </div>
        <UzButton variant="outline" onClick={() => void registerQuery.refetch()}>
          Refresh
        </UzButton>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterChip
          label={`All (${allRows.length})`}
          active={paidFilter === 'ALL'}
          onClick={() => setPaidFilter('ALL')}
        />
        <FilterChip
          label={`GST paid (${paidCount})`}
          active={paidFilter === 'PAID'}
          onClick={() => setPaidFilter('PAID')}
        />
        <FilterChip
          label={`GST unpaid (${unpaidCount})`}
          active={paidFilter === 'UNPAID'}
          onClick={() => setPaidFilter('UNPAID')}
        />
      </div>

      <UzCard padding="none">
        {registerQuery.isLoading ? (
          <p className="p-6 text-sm text-zinc-500">Loading register...</p>
        ) : registerQuery.isError ? (
          <p className="p-6 text-sm text-red-600">Could not load the register.</p>
        ) : allRows.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">No sales in this range.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">No invoices match this filter.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                  <th className="px-3 py-3">S.No</th>
                  <th className="px-3 py-3">Proforma No.</th>
                  <th className="px-3 py-3">GST Invoice No.</th>
                  <th className="px-3 py-3">Date</th>
                  <th className="px-3 py-3">Customer</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3">POS (State)</th>
                  <th className="px-3 py-3">Service</th>
                  <th className="px-3 py-3 text-right">Non-Taxable</th>
                  <th className="px-3 py-3 text-right">Taxable</th>
                  <th className="px-3 py-3 text-right">CGST %</th>
                  <th className="px-3 py-3 text-right">CGST</th>
                  <th className="px-3 py-3 text-right">SGST %</th>
                  <th className="px-3 py-3 text-right">SGST</th>
                  <th className="px-3 py-3 text-right">IGST %</th>
                  <th className="px-3 py-3 text-right">IGST</th>
                  <th className="px-3 py-3 text-right">Total GST</th>
                  <th className="px-3 py-3 text-right">Gross Sale</th>
                  <th className="px-3 py-3">GST paid</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const isCredit = row.kind === 'CREDIT_NOTE';
                  const tone = isCredit ? 'text-red-600' : 'text-zinc-700';
                  const num = (v: string): string => formatInrPaise(v);
                  return (
                    <tr
                      key={`${row.kind}-${row.number}-${i}`}
                      className="border-b border-zinc-100 last:border-0"
                    >
                      <td className={`px-3 py-3 tabular-nums ${tone}`}>{i + 1}</td>
                      <td className={`px-3 py-3 tabular-nums ${tone}`}>
                        {row.proformaNumber ?? '-'}
                      </td>
                      <td className={`px-3 py-3 tabular-nums ${tone}`}>
                        {row.gstInvoiceNumber ?? '-'}
                      </td>
                      <td className={`px-3 py-3 tabular-nums ${tone}`}>
                        {formatDateDMY(row.date)}
                      </td>
                      <td className={`px-3 py-3 ${tone}`}>{row.customerName ?? '-'}</td>
                      <td className={`px-3 py-3 ${tone}`}>{row.customerType}</td>
                      <td className={`px-3 py-3 ${tone}`}>{row.posState ?? '-'}</td>
                      <td className={`px-3 py-3 ${tone}`}>{row.serviceType ?? '-'}</td>
                      <td className={`px-3 py-3 text-right tabular-nums ${tone}`}>
                        {num(row.nonTaxablePaise)}
                      </td>
                      <td className={`px-3 py-3 text-right tabular-nums ${tone}`}>
                        {num(row.taxablePaise)}
                      </td>
                      <td className={`px-3 py-3 text-right tabular-nums ${tone}`}>
                        {row.cgstRate}%
                      </td>
                      <td className={`px-3 py-3 text-right tabular-nums ${tone}`}>
                        {num(row.cgstPaise)}
                      </td>
                      <td className={`px-3 py-3 text-right tabular-nums ${tone}`}>
                        {row.sgstRate}%
                      </td>
                      <td className={`px-3 py-3 text-right tabular-nums ${tone}`}>
                        {num(row.sgstPaise)}
                      </td>
                      <td className={`px-3 py-3 text-right tabular-nums ${tone}`}>
                        {row.igstRate}%
                      </td>
                      <td className={`px-3 py-3 text-right tabular-nums ${tone}`}>
                        {num(row.igstPaise)}
                      </td>
                      <td className={`px-3 py-3 text-right tabular-nums ${tone}`}>
                        {num(row.totalGstPaise)}
                      </td>
                      <td
                        className={`px-3 py-3 text-right font-medium tabular-nums ${
                          isCredit ? 'text-red-600' : 'text-zinc-900'
                        }`}
                      >
                        {num(row.grossSalePaise)}
                      </td>
                      <td className="px-3 py-3">
                        {isCredit || !row.invoiceId ? (
                          <span className="text-zinc-400">-</span>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <UzBadge tone={row.gstPaid ? 'success' : 'warning'}>
                                {row.gstPaid ? 'Paid' : 'Unpaid'}
                              </UzBadge>
                              {row.gstPaid ? (
                                <UzButton
                                  size="sm"
                                  variant="ghost"
                                  loading={markPaid.isPending}
                                  onClick={() =>
                                    markPaid.mutate(
                                      { invoiceId: row.invoiceId as string, body: { paid: false } },
                                      { onSuccess: () => toast.success('Marked unpaid') },
                                    )
                                  }
                                >
                                  Mark unpaid
                                </UzButton>
                              ) : (
                                <UzButton
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    setPaidDialog({
                                      id: row.invoiceId as string,
                                      number: row.number,
                                    })
                                  }
                                >
                                  Mark paid
                                </UzButton>
                              )}
                            </div>
                            {row.gstPaid && row.gstPaidAt ? (
                              <p className="mt-1 text-xs text-zinc-500">
                                Paid {formatDMY(row.gstPaidAt)}
                              </p>
                            ) : null}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {data && (
                <tfoot>
                  <tr className="border-t border-zinc-200 bg-zinc-50 text-sm font-semibold text-zinc-900">
                    <td className="px-3 py-3" colSpan={9}>
                      {paidFilter === 'ALL' ? 'Net output tax' : 'Net output tax (filtered)'}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(footer.taxablePaise)}
                    </td>
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(footer.cgstPaise)}
                    </td>
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(footer.sgstPaise)}
                    </td>
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(footer.igstPaise)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(footer.totalPaise)}
                    </td>
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </UzCard>

      {data && paidFilter === 'ALL' && (allRows.length > 0 || data.bySlab.length > 0) && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SummaryCard title="By slab" rows={data.bySlab} />
          <SummaryCard title="By place of supply" rows={data.byPlaceOfSupply} />
        </div>
      )}

      <GstInvoicePaidDialog
        key={`gst-paid-${paidDialog?.id ?? 'none'}`}
        open={paidDialog !== null}
        subject={paidDialog ? `Invoice ${paidDialog.number}` : ''}
        pending={markPaid.isPending}
        onCancel={() => setPaidDialog(null)}
        onConfirm={({ paidAt }) => {
          if (!paidDialog) return;
          markPaid.mutate(
            { invoiceId: paidDialog.id, body: { paid: true, paidAt: dateInputToIso(paidAt) } },
            {
              onSuccess: () => {
                setPaidDialog(null);
                toast.success('Marked paid');
              },
            },
          );
        }}
      />
    </PageShell>
  );
};

const SummaryCard = ({
  title,
  rows,
}: {
  title: string;
  rows: GstOutputRegisterSummaryRow[];
}): React.JSX.Element => (
  <UzCard title={title} padding="none">
    {rows.length === 0 ? (
      <p className="p-6 text-sm text-zinc-500">No data.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
              <th className="px-4 py-3">Bucket</th>
              <th className="px-4 py-3 text-right">Taxable</th>
              <th className="px-4 py-3 text-right">CGST</th>
              <th className="px-4 py-3 text-right">SGST</th>
              <th className="px-4 py-3 text-right">IGST</th>
              <th className="px-4 py-3 text-right">Total GST</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-b border-zinc-100 last:border-0">
                <td className="px-4 py-3 text-zinc-700">{row.label}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-zinc-700 tabular-nums">
                  {formatInrPaise(row.taxablePaise)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-zinc-700 tabular-nums">
                  {formatInrPaise(row.cgstPaise)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-zinc-700 tabular-nums">
                  {formatInrPaise(row.sgstPaise)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-zinc-700 tabular-nums">
                  {formatInrPaise(row.igstPaise)}
                </td>
                <td className="px-4 py-3 text-right font-medium whitespace-nowrap text-zinc-900 tabular-nums">
                  {formatInrPaise(row.totalPaise)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </UzCard>
);
